import React, { useState, useEffect, useRef } from "react";
import { FaArrowLeft, FaDesktop, FaMobileAlt, FaUndo, FaRedo, FaSave, FaLayerGroup, FaImage, FaFont, FaTrash, FaAlignLeft, FaAlignCenter, FaAlignRight } from "react-icons/fa";

// Premium Dark Theme Constants
const THEME = {
    bg: "#0f111a",
    sidebarBg: "#1a1d21",
    sidebarBorder: "1px solid rgba(255,255,255,0.1)",
    text: "#e9ecff",
    textMuted: "#9ba5c0",
    accent: "#764ae2",
    accentHover: "#5f3bc2",
    inputBg: "#2a2d3d", // Lighter background for inputs
    inputBorder: "1px solid rgba(255,255,255,0.15)",
    canvasBg: "#f0f2f5",
    danger: "#ff4d4f",
};

// --- Components ---

const DraggableElement = ({ element, isSelected, onSelect, onUpdate }) => {
    const style = {
        padding: element.style.padding || "10px",
        margin: element.style.margin || "0px",
        backgroundColor: element.style.backgroundColor || "transparent",
        color: element.style.color || "inherit",
        fontSize: element.style.fontSize ? `${element.style.fontSize}px` : "inherit",
        textAlign: element.style.textAlign || "left",
        fontWeight: element.style.fontWeight || "normal",
        borderRadius: element.style.borderRadius ? `${element.style.borderRadius}px` : "0px",
        border: isSelected ? `2px solid ${THEME.accent}` : "2px solid transparent",
        cursor: "pointer",
        position: "relative",
        minHeight: "40px",
        ...element.style // Apply other custom styles
    };

    return (
        <div
            onClick={(e) => { e.stopPropagation(); onSelect(element); }}
            style={style}
            className="canvas-element"
        >
            {element.type === "text" && (
                <div dangerouslySetInnerHTML={{ __html: element.content || "Double click to edit" }} />
            )}
            {element.type === "image" && (
                <img
                    src={element.content || "https://via.placeholder.com/150"}
                    alt="placeholder"
                    style={{ maxWidth: "100%", display: "block" }}
                />
            )}
            {element.type === "box" && (
                <div style={{ minHeight: "50px", border: "1px dashed #ccc", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                    {element.content || "Box Container"}
                </div>
            )}
        </div>
    );
};

const Canvas = ({ elements, pageStyle, onSelect, viewMode }) => {
    return (
        <div
            style={{
                flex: 1,
                background: "#090b10",
                backgroundImage: "radial-gradient(#2a2d3d 1px, transparent 1px)",
                backgroundSize: "20px 20px",
                display: "flex",
                justifyContent: "center",
                padding: "40px",
                overflow: "auto",
                transition: "all 0.3s ease"
            }}
            onClick={() => onSelect(null)}
        >
            <div
                style={{
                    width: viewMode === "mobile" ? "375px" : "100%",
                    maxWidth: viewMode === "mobile" ? "375px" : "1200px",
                    background: pageStyle.backgroundColor || "white",
                    minHeight: "800px",
                    boxShadow: "0 0 40px rgba(0,0,0,0.5)",
                    borderRadius: viewMode === "mobile" ? "20px" : "4px",
                    position: "relative",
                    padding: pageStyle.padding || "40px",
                    transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                    color: "#333",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {elements.length === 0 && (
                    <div style={{ textAlign: "center", color: "#ccc", marginTop: "100px" }}>
                        <h3>Canvas is Empty</h3>
                        <p>Click elements on the left to add them here.</p>
                    </div>
                )}
                {elements.map(el => (
                    <DraggableElement
                        key={el.id}
                        element={el}
                        isSelected={el.isSelected}
                        onSelect={onSelect}
                    />
                ))}
            </div>
        </div>
    );
};

const LeftSidebar = ({ onAdd }) => {
    const tools = [
        { type: "text", label: "Text", icon: <FaFont /> },
        { type: "image", label: "Image", icon: <FaImage /> },
        { type: "box", label: "Box", icon: <FaLayerGroup /> },
    ];

    return (
        <div style={{ width: "260px", background: THEME.sidebarBg, borderRight: THEME.sidebarBorder, display: "flex", flexDirection: "column", zIndex: 10 }}>
            <div style={{ padding: "16px", borderBottom: THEME.sidebarBorder, fontWeight: "600", color: THEME.text, fontSize: "14px", letterSpacing: "0.5px" }}>
                ADD ELEMENTS
            </div>
            <div style={{ padding: "16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                {tools.map(item => (
                    <div
                        key={item.label}
                        onClick={() => onAdd(item.type)}
                        style={{
                            padding: "16px",
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.05)",
                            borderRadius: "8px",
                            textAlign: "center",
                            cursor: "pointer",
                            fontSize: "12px",
                            color: THEME.text,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
                        }}
                    >
                        <div style={{ fontSize: "18px", color: THEME.accent }}>{item.icon}</div>
                        {item.label}
                    </div>
                ))}
            </div>
        </div>
    );
};

const RightSidebar = ({ selectedElement, onUpdate, onDelete, pageStyle, onUpdatePage }) => {
    const labelStyle = { display: "block", marginBottom: "8px", fontSize: "12px", fontWeight: "500", color: THEME.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" };
    const inputStyle = {
        width: "100%",
        padding: "8px 12px",
        background: THEME.inputBg,
        border: THEME.inputBorder,
        borderRadius: "6px",
        color: THEME.text,
        fontSize: "14px",
        outline: "none"
    };

    // Helper to update style properties safely
    const updateStyle = (key, value) => {
        onUpdate({
            ...selectedElement,
            style: { ...selectedElement.style, [key]: value }
        });
    };

    const updateContent = (value) => {
        onUpdate({ ...selectedElement, content: value });
    };

    // Custom scrollbar styles
    const scrollbarStyles = {
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(255,255,255,0.2) transparent",
    };

    if (!selectedElement) {
        return (
            <div style={{ width: "320px", background: THEME.sidebarBg, borderLeft: THEME.sidebarBorder, display: "flex", flexDirection: "column", zIndex: 10 }}>
                <div style={{ padding: "16px", borderBottom: THEME.sidebarBorder, fontWeight: "600", color: THEME.text, fontSize: "14px", letterSpacing: "0.5px" }}>
                    PAGE PROPERTIES
                </div>
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "24px" }}>
                    <div>
                        <label style={labelStyle}>Page Background</label>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <input
                                type="color"
                                value={pageStyle.backgroundColor || "#ffffff"}
                                onChange={(e) => onUpdatePage({ ...pageStyle, backgroundColor: e.target.value })}
                                style={{ width: "40px", height: "40px", padding: 0, border: "none", borderRadius: "4px", cursor: "pointer" }}
                            />
                            <input
                                type="text"
                                value={pageStyle.backgroundColor || "#ffffff"}
                                onChange={(e) => onUpdatePage({ ...pageStyle, backgroundColor: e.target.value })}
                                style={{ ...inputStyle, flex: 1 }}
                            />
                        </div>
                    </div>
                    <div>
                        <label style={labelStyle}>Page Padding</label>
                        <input
                            type="text"
                            value={pageStyle.padding || "40px"}
                            onChange={(e) => onUpdatePage({ ...pageStyle, padding: e.target.value })}
                            style={inputStyle}
                        />
                    </div>
                    <div style={{ padding: "20px", background: "rgba(255,255,255,0.05)", borderRadius: "8px", textAlign: "center" }}>
                        <p style={{ fontSize: "13px", color: THEME.textMuted, margin: 0 }}>
                            Select an element on the canvas to edit its specific properties.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: "320px", background: THEME.sidebarBg, borderLeft: THEME.sidebarBorder, display: "flex", flexDirection: "column", zIndex: 10 }}>
            <div style={{ padding: "16px", borderBottom: THEME.sidebarBorder, fontWeight: "600", display: "flex", justifyContent: "space-between", alignItems: "center", color: THEME.text }}>
                <span style={{ fontSize: "14px", letterSpacing: "0.5px" }}>EDIT {selectedElement.type.toUpperCase()}</span>
                <button
                    onClick={onDelete}
                    style={{ background: "transparent", border: "none", color: THEME.danger, cursor: "pointer", padding: "4px" }}
                    title="Delete Element"
                >
                    <FaTrash />
                </button>
            </div>

            <div
                style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    overflowY: "auto",
                    ...scrollbarStyles
                }}
                className="custom-scrollbar"
            >
                <style>
                    {`
                        .custom-scrollbar::-webkit-scrollbar {
                            width: 6px;
                        }
                        .custom-scrollbar::-webkit-scrollbar-track {
                            background: transparent;
                        }
                        .custom-scrollbar::-webkit-scrollbar-thumb {
                            background-color: rgba(255,255,255,0.2);
                            border-radius: 3px;
                        }
                    `}
                </style>

                {/* Content Editor */}
                <div>
                    <label style={labelStyle}>Content</label>
                    {selectedElement.type === "text" ? (
                        <textarea
                            value={selectedElement.content || ""}
                            onChange={(e) => updateContent(e.target.value)}
                            style={{ ...inputStyle, minHeight: "80px", fontFamily: "inherit" }}
                            placeholder="Enter text..."
                        />
                    ) : (
                        <input
                            type="text"
                            value={selectedElement.content || ""}
                            onChange={(e) => updateContent(e.target.value)}
                            style={inputStyle}
                            placeholder={selectedElement.type === "image" ? "Image URL" : "Content"}
                        />
                    )}
                </div>

                {/* Style Editors */}
                <div>
                    <label style={labelStyle}>Background Color</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <input
                            type="color"
                            value={selectedElement.style.backgroundColor || "transparent"}
                            onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                            style={{ width: "40px", height: "40px", padding: 0, border: "none", borderRadius: "4px", cursor: "pointer" }}
                        />
                        <input
                            type="text"
                            value={selectedElement.style.backgroundColor || "transparent"}
                            onChange={(e) => updateStyle("backgroundColor", e.target.value)}
                            style={{ ...inputStyle, flex: 1 }}
                        />
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>Text Color</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <input
                            type="color"
                            value={selectedElement.style.color || "#000000"}
                            onChange={(e) => updateStyle("color", e.target.value)}
                            style={{ width: "40px", height: "40px", padding: 0, border: "none", borderRadius: "4px", cursor: "pointer" }}
                        />
                        <input
                            type="text"
                            value={selectedElement.style.color || "inherit"}
                            onChange={(e) => updateStyle("color", e.target.value)}
                            style={{ ...inputStyle, flex: 1 }}
                        />
                    </div>
                </div>

                {selectedElement.type === "text" && (
                    <>
                        <div>
                            <label style={labelStyle}>Font Size (px)</label>
                            <input
                                type="number"
                                value={parseInt(selectedElement.style.fontSize) || 16}
                                onChange={(e) => updateStyle("fontSize", e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Alignment</label>
                            <div style={{ display: "flex", gap: "8px", background: THEME.inputBg, padding: "4px", borderRadius: "6px", border: THEME.inputBorder }}>
                                {["left", "center", "right"].map(align => (
                                    <button
                                        key={align}
                                        onClick={() => updateStyle("textAlign", align)}
                                        style={{
                                            flex: 1,
                                            background: selectedElement.style.textAlign === align ? THEME.accent : "transparent",
                                            border: "none",
                                            borderRadius: "4px",
                                            color: "white",
                                            padding: "6px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        {align === "left" && <FaAlignLeft />}
                                        {align === "center" && <FaAlignCenter />}
                                        {align === "right" && <FaAlignRight />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                <div>
                    <label style={labelStyle}>Padding</label>
                    <input
                        type="text"
                        value={selectedElement.style.padding || "10px"}
                        onChange={(e) => updateStyle("padding", e.target.value)}
                        style={inputStyle}
                    />
                </div>

                <div>
                    <label style={labelStyle}>Margin</label>
                    <input
                        type="text"
                        value={selectedElement.style.margin || "0px"}
                        onChange={(e) => updateStyle("margin", e.target.value)}
                        style={inputStyle}
                    />
                </div>

                <div>
                    <label style={labelStyle}>Border Radius (px)</label>
                    <input
                        type="number"
                        value={parseInt(selectedElement.style.borderRadius) || 0}
                        onChange={(e) => updateStyle("borderRadius", e.target.value)}
                        style={inputStyle}
                    />
                </div>

            </div>
        </div>
    );
};

export default function VisualBuilder({ initialContent, onSave, onCancel }) {
    const [viewMode, setViewMode] = useState("desktop");
    const [elements, setElements] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [pageStyle, setPageStyle] = useState({ backgroundColor: "#ffffff", padding: "40px" });

    // Initialize from props
    useEffect(() => {
        if (initialContent && initialContent.html) {
            // In a real app, we'd parse the HTML back to elements.
            // For this demo/MVP, we'll start fresh or try to load if it's JSON
            try {
                const parsed = JSON.parse(initialContent.html);
                if (parsed.elements) {
                    setElements(parsed.elements);
                    setPageStyle(parsed.pageStyle || {});
                }
            } catch (e) {
                // If it's raw HTML string, we might just add it as a text block
                if (initialContent.html.trim()) {
                    setElements([{
                        id: Date.now(),
                        type: "text",
                        content: initialContent.html,
                        style: {}
                    }]);
                }
            }
        }
    }, [initialContent]);

    const handleAddElement = (type) => {
        const newElement = {
            id: Date.now(),
            type,
            content: type === "text" ? "New Text Block" : type === "image" ? "https://via.placeholder.com/300x200" : "New Box",
            style: {
                padding: "10px",
                margin: "0px 0px 10px 0px",
                fontSize: 16,
                color: "#333333",
                backgroundColor: type === "box" ? "#f9f9f9" : "transparent"
            }
        };
        setElements([...elements, newElement]);
        setSelectedId(newElement.id);
    };

    const handleSelect = (element) => {
        setSelectedId(element ? element.id : null);
    };

    const handleUpdateElement = (updatedElement) => {
        setElements(elements.map(el => el.id === updatedElement.id ? updatedElement : el));
    };

    const handleDeleteElement = () => {
        if (selectedId) {
            setElements(elements.filter(el => el.id !== selectedId));
            setSelectedId(null);
        }
    };

    const handleSave = () => {
        // Serialize state to JSON string to store in the 'html' field
        // This allows us to restore the editor state later
        const stateToSave = {
            elements,
            pageStyle
        };

        // We also generate a preview HTML string for the actual display
        // In a real app, you'd generate proper HTML structure here
        const previewHtml = JSON.stringify(stateToSave);

        onSave({
            html: previewHtml,
            lastModified: Date.now()
        });
    };

    const selectedElement = elements.find(el => el.id === selectedId);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", width: "100vw", position: "fixed", top: 0, left: 0, zIndex: 9999, background: THEME.bg, fontFamily: "'Inter', sans-serif" }}>
            {/* Top Bar */}
            <div
                style={{
                    height: "60px",
                    background: "#15171e",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 20px",
                    justifyContent: "space-between",
                    zIndex: 20
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                    <button
                        onClick={onCancel}
                        style={{ background: "transparent", border: "none", color: "#e9ecff", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: "500", opacity: 0.8, transition: "opacity 0.2s" }}
                    >
                        <FaArrowLeft /> Back
                    </button>
                    <div style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.1)" }}></div>
                    <span style={{ fontWeight: "600", fontSize: "16px", letterSpacing: "0.5px" }}>Visual Builder</span>
                </div>

                <div style={{ display: "flex", gap: "4px", background: "rgba(0,0,0,0.3)", padding: "4px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <button
                        onClick={() => setViewMode("desktop")}
                        style={{
                            background: viewMode === "desktop" ? "rgba(255,255,255,0.1)" : "transparent",
                            border: "none",
                            color: viewMode === "desktop" ? "white" : "#888",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            cursor: "pointer"
                        }}
                    >
                        <FaDesktop />
                    </button>
                    <button
                        onClick={() => setViewMode("mobile")}
                        style={{
                            background: viewMode === "mobile" ? "rgba(255,255,255,0.1)" : "transparent",
                            border: "none",
                            color: viewMode === "mobile" ? "white" : "#888",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            cursor: "pointer"
                        }}
                    >
                        <FaMobileAlt />
                    </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <button
                        onClick={handleSave}
                        style={{
                            background: "linear-gradient(135deg, #764ae2 0%, #4d35ff 100%)",
                            border: "none",
                            color: "white",
                            padding: "8px 24px",
                            borderRadius: "6px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            boxShadow: "0 4px 12px rgba(77, 53, 255, 0.3)"
                        }}
                    >
                        <FaSave /> Save Changes
                    </button>
                </div>
            </div>

            {/* Main Workspace */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
                <LeftSidebar onAdd={handleAddElement} />
                <Canvas
                    elements={elements.map(el => ({ ...el, isSelected: el.id === selectedId }))}
                    pageStyle={pageStyle}
                    onSelect={handleSelect}
                    viewMode={viewMode}
                />
                <RightSidebar
                    selectedElement={selectedElement}
                    onUpdate={handleUpdateElement}
                    onDelete={handleDeleteElement}
                    pageStyle={pageStyle}
                    onUpdatePage={setPageStyle}
                />
            </div>
        </div>
    );
}
